import React, { useState, Dispatch, SetStateAction } from "react";
import {
  Box,
  Modal,
  Grid,
  TextField,
  Typography,
  InputLabel,
  MenuItem,
  FormControl,
  Select,
  SelectChangeEvent,
  Button,
} from "@mui/material";
import { FeedbackModalStyle } from "#next/components/modals/styles";
import { trpc } from "#next/utils/trpc";
import Image from "next/image";

const ComponentSelect = ({
  component,
  setComponent,
  disabled,
}: {
  component: string | null;
  setComponent: Dispatch<SetStateAction<string | null>>;
  disabled: boolean;
}) => {
  const handleChange = (event: SelectChangeEvent) => {
    setComponent(event.target.value);
  };

  return (
    <FormControl sx={{ m: 1, minWidth: 140 }} size="small">
      <InputLabel id="component-select-label">Component</InputLabel>
      <Select
        labelId="component-select-label"
        id="demo-select-small"
        value={component ?? undefined}
        label="Feature"
        onChange={handleChange}
        disabled={disabled}
      >
        <MenuItem value={undefined}>
          <em>None</em>
        </MenuItem>
        <MenuItem value="project editor">Project Editor</MenuItem>
        <MenuItem value="project catalog">Catalog</MenuItem>
        <MenuItem value="project explorer">Explorer</MenuItem>
        <MenuItem value="boolean search">Boolean Search</MenuItem>
        <MenuItem value="media search">Media Search</MenuItem>
        <MenuItem value="user search">User Search</MenuItem>
        <MenuItem value="automated reports">Automated Reports</MenuItem>
        <MenuItem value="project subscriptions">Subscriptions</MenuItem>
      </Select>
    </FormControl>
  );
};

export const FeedbackModal = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);
  const [selectedComponent, setSelectedComponent] = useState<string | null>(
    null,
  );
  const [submitted, setSubmitted] = useState(false);

  const userQuery = trpc.user.me.useQuery(undefined, {
    trpc: { context: { skipBatch: true } },
  });

  const sendFeedback = trpc.actions.sendFeedback.useMutation();

  if (!userQuery.data) {
    return <></>;
  }

  const handleSubmit = () => {
    const slackMessage = `Feedback from ${userQuery.data.email} (${userQuery.data.organization.name}). ${selectedComponent ? `Primarily related to the ${selectedComponent}. ` : ``}Full message: ${feedbackMessage}`;
    sendFeedback.mutate({
      slackMessage: slackMessage,
    });
    setSubmitted(true);
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={FeedbackModalStyle}>
        <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
          <Image
            src="/images/pyrraLogo.jpg"
            width={75}
            height={75}
            alt="Pyrra Technologies logo"
          />
        </Box>
        <Typography variant="h3" sx={{ textAlign: "center", mb: 2 }}>
          Share feedback with the Pyrra team!
        </Typography>
        <Grid container direction="column">
          <Grid
            item
            sx={{
              justifyContent: "space-between",
              alignItems: "center",
              display: "flex",
              width: "100%",
              mb: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontSize: 16 }}
            >{`From: ${userQuery.data?.email}`}</Typography>
            <ComponentSelect
              component={selectedComponent}
              setComponent={setSelectedComponent}
              disabled={submitted}
            />
          </Grid>
          <Grid item>
            <TextField
              label="Leave your feedback here..."
              size="small"
              value={
                !submitted
                  ? feedbackMessage
                  : `${feedbackMessage} --- SUBMITTED, THANK YOU!`
              }
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setFeedbackMessage(event.target.value);
              }}
              sx={{ width: "100%" }}
              multiline
              minRows={10}
              maxRows={10}
              disabled={submitted}
            />
          </Grid>
          <Grid
            item
            sx={{ width: "100%", display: "flex", justifyContent: "flex-end" }}
          >
            <Button
              variant="outlined"
              sx={{ mt: 2 }}
              onClick={handleSubmit}
              disabled={
                submitted ||
                !feedbackMessage ||
                feedbackMessage.length === 0 ||
                feedbackMessage.length > 500
              }
            >
              Share
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
};
